import { Button } from '@/components/ui/button';
import { Edit, Eye, Info, Trash2, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Asset } from '../mockAssets';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { renderDate, cleanDateAndTimeObject } from '../../../../../shared/renderDate';
import { Loader2 } from 'lucide-react';
import HelpPopoverHover from '@/components/HelpPopoverHover';
import { vendorScoreService } from '@/services/vendorScoreService';
import { MiniVendorScoreChart } from '@/components/MiniVendorScoreChart';

export interface AssetActionsProps {
  onEdit?: (row: Asset) => void;
  onView?: (row: Asset) => void;
  onRecover?: (row: Asset) => Promise<void>;
  isDeletedView?: boolean;
  isReadOnly?: boolean; // New flag for read-only mode
  recoveringAssetId?: string | null;
  userIdToName?: Record<string, string> | ((userId: string) => string);
  helpData: any;
}

type AssetRow = Asset & Record<string, any>;

type UpguardScoreState = {
  loading: boolean;
  score?: number | null;
  captureDate?: Date;
  error?: string;
};

type UpguardScoreCacheEntry = {
  score: number | null | undefined;
  captureDateISO?: string;
  fetchedAt: number;
};

const UP_GUARD_SCORE_CACHE_TTL = 5 * 60 * 1000;

const upguardScoreCache = new Map<string, UpguardScoreCacheEntry>();

const getCachedUpguardScore = (vendorWebsite: string): number | null => {
  if (!vendorWebsite) return null;
  const cacheKey = vendorWebsite.toLowerCase();
  const cached = upguardScoreCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < UP_GUARD_SCORE_CACHE_TTL) {
    return cached.score ?? null;
  }
  return null; // Not cached or expired
};

const UpguardScoreCell: React.FC<{ asset: AssetRow }> = ({ asset }) => {
  const vendorWebsite = typeof asset.vendorWebsite === 'string' ? asset.vendorWebsite.trim() : '';
  const vendorMonitoringSource = (asset as AssetRow & { vendorMonitoringEnabled?: boolean | string }).vendorMonitoringEnabled;
  const vendorMonitoringRaw = vendorMonitoringSource ?? asset.vendorManagementNeeded;

  let monitoringEnabled = false;
  if (typeof vendorMonitoringRaw === 'string') {
    const normalized = vendorMonitoringRaw.trim().toLowerCase();
    monitoringEnabled = normalized === 'yes' || normalized === 'true' || normalized === 'enabled';
  } else if (typeof vendorMonitoringRaw === 'boolean') {
    monitoringEnabled = vendorMonitoringRaw;
  }

  const shouldFetch = monitoringEnabled && !!vendorWebsite;

  const [state, setState] = React.useState<UpguardScoreState>({
    loading: shouldFetch,
  });

  React.useEffect(() => {
    if (!shouldFetch) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }

    const cacheKey = vendorWebsite.toLowerCase();
    const cached = upguardScoreCache.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < UP_GUARD_SCORE_CACHE_TTL) {
      setState({
        loading: false,
        score: cached.score ?? null,
        captureDate: cached.captureDateISO ? new Date(cached.captureDateISO) : undefined,
      });
      return;
    }

    let cancelled = false;

    const fetchScore = async () => {
      setState({ loading: true });
      try {
        const history = await vendorScoreService.getVendorScoreHistory(vendorWebsite, 1);
        if (cancelled) return;

        const latest = history[0];
        const captureDate = latest ? cleanDateAndTimeObject(latest.captureDate) : null;
        const score = latest?.capturedScore ?? null;

        setState({
          loading: false,
          score,
          captureDate: captureDate ?? undefined,
        });

        upguardScoreCache.set(cacheKey, {
          score,
          captureDateISO: captureDate ? captureDate.toISOString() : undefined,
          fetchedAt: Date.now(),
        });
      } catch (error) {
        if (cancelled) return;
        console.error('[AssetRegister] Failed to fetch UpGuard score:', error);
        setState({
          loading: false,
          error: 'Failed to load',
        });
      }
    };

    fetchScore();

    return () => {
      cancelled = true;
    };
  }, [shouldFetch, vendorWebsite]);

  if (!shouldFetch) {
    return <span className="text-center w-full block text-muted-foreground">Not monitored</span>;
  }

  if (state.loading) {
    return (
      <div className="flex justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (state.error) {
    return <span className="text-center w-full block text-destructive text-xs">{state.error}</span>;
  }

  if (state.score === null || state.score === undefined) {
    return <span className="text-center w-full block text-muted-foreground">No data</span>;
  }

  const formattedDate = state.captureDate ? cleanDateAndTimeObject(state.captureDate).toDateString() : 'Unknown';

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className="justify-center px-2 py-1 w-full">
            {state.score}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col gap-1 text-xs">
            <span>Last checked</span>
            <span>{formattedDate}</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const getAssetColumns = (onActions: AssetActionsProps): ColumnDef<Asset, any>[] => [
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.category']}>Category</HelpPopoverHover>
      </span>
    ),
    accessorKey: 'category',
    cell: ({ getValue }) => <span className="font-medium text-center w-full block">{getValue()}</span>,
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold">
        <HelpPopoverHover config={onActions.helpData?.['table.header.description']}>Description</HelpPopoverHover>
      </span>
    ),
    accessorKey: 'description',
    cell: ({ getValue }) => <span>{getValue()}</span>,
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.criticality']}>Criticality</HelpPopoverHover>
      </span>
    ),
    accessorKey: 'criticality',
    cell: ({ getValue }) => <div className="text-center w-full"><Badge variant={getValue().toLowerCase()}>{getValue()}</Badge></div>,
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.risk']}>Risk</HelpPopoverHover>
      </span>
    ),
    accessorKey: 'risk',
    cell: ({ getValue }) => <div className="text-center w-full"><Badge variant={getValue().toLowerCase()}>{getValue()}</Badge></div>,
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.linkedrisks']}>Linked Risks</HelpPopoverHover>
      </span>
    ),
    id: 'linkedRisks',
    cell: ({ row }) => {
      const asset = row.original as AssetRow;
      const risks = Array.isArray((asset as any).risks) ? (asset as any).risks : [];
      const count = risks.length;
      return (
        <span className="text-center w-full block">
          {count === 0 ? 'None' : `${count} linked`}
        </span>
      );
    },
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.owner']}>Owner</HelpPopoverHover>
      </span>
    ),
    accessorKey: 'owner',
    cell: ({ getValue }) => {
      const userId = getValue();
      if (!userId) return <span className="text-center w-full block">Unassigned</span>;

      // If userIdToName is not provided, just display the raw value
      if (!onActions.userIdToName) return <span className="text-center w-full block">{userId}</span>;

      let displayName;
      if (typeof onActions.userIdToName === 'function') {
        // If it's a function, call the function
        displayName = onActions.userIdToName(userId) || 'Unknown user';
      } else {
        // If it's an object, use property access
        displayName = onActions.userIdToName[userId] || 'Unknown user';
      }

      return (
        <span className="text-center w-full block">{displayName}</span>
      );
    },
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.nextreviewdate']}>Next Review Date</HelpPopoverHover>
      </span>
    ),
    accessorKey: 'nextReviewDate',
    cell: ({ getValue }) => <div className="text-center w-full block">{renderDate(getValue())}</div>,
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.upguardscore']}>UpGuard Score</HelpPopoverHover>
      </span>
    ),
    accessorFn: (row) => getCachedUpguardScore(row.vendorWebsite),
    id: 'upguardScore',
    cell: ({ row }) => <UpguardScoreCell asset={row.original as AssetRow} />,
  },
    {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.scorechart']}>Score Chart</HelpPopoverHover>
      </span>
    ),
    accessorFn: (row) => getCachedUpguardScore(row.vendorWebsite),
    id: 'scoreChart',
    cell: ({ row }) => {
      const asset = row.original as AssetRow;
      const vendorWebsite = typeof asset.vendorWebsite === 'string' ? asset.vendorWebsite.trim() : '';
      const vendorMonitoringSource = (asset as AssetRow & { vendorMonitoringEnabled?: boolean | string }).vendorMonitoringEnabled;
      const vendorMonitoringRaw = vendorMonitoringSource ?? asset.vendorManagementNeeded;

      let monitoringEnabled = false;
      if (typeof vendorMonitoringRaw === 'string') {
        const normalized = vendorMonitoringRaw.trim().toLowerCase();
        monitoringEnabled = normalized === 'yes' || normalized === 'true' || normalized === 'enabled';
      } else if (typeof vendorMonitoringRaw === 'boolean') {
        monitoringEnabled = vendorMonitoringRaw;
      }

      const shouldShowChart = monitoringEnabled && !!vendorWebsite;

      if (!shouldShowChart) {
        return <span className="text-center w-full block text-muted-foreground">Not monitored</span>;
      }

      return (
        <div className="flex justify-center items-center">
          <MiniVendorScoreChart vendorWebsite={vendorWebsite} />
        </div>
      );
    },
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.actions']}>Actions</HelpPopoverHover>
      </span>
    ),
    id: 'actions',
    cell: ({ row }) => {
      if (onActions.isDeletedView) {
        return (
          <div className="flex gap-1 justify-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onActions.onRecover?.(row.original)}
                    disabled={onActions.recoveringAssetId === row.original.id}
                  >
                    {onActions.recoveringAssetId === row.original.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Recover this asset</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      }

      return (
        <div className="flex gap-1 justify-center">
          {/* Show Eye for read-only mode, Edit for regular mode */}
          {onActions.isReadOnly ? (
            <Button variant="ghost" size="icon" onClick={(e) => {
              e.stopPropagation(); // Prevent row click from triggering
              onActions.onView?.(row.original);
            }}>
              <Eye className="h-4 w-4" />
            </Button>
          ) : (
            <Button variant="ghost" size="icon" onClick={(e) => {
              e.stopPropagation(); // Prevent row click from triggering
              onActions.onEdit?.(row.original);
            }}>
              <Edit className="h-4 w-4" />
            </Button>
          )}
        </div>
      );
    },
    enableSorting: false,
  },
];
